import { z } from 'zod'

export const RedactOptionSchema = z.enum([
  'account_number',
  'age',
  'bank_account',
  'cardinal',
  'credit_card',
  'credit_card_expiration',
  'cvv',
  'date',
  'date_interval',
  'dob',
  'driver_license',
  'email_address',
  'event',
  'filename',
  'gender_sexuality',
  'healthcare_number',
  'ip_address',
  'location',
  'location_address',
  'location_city',
  'location_coordinate',
  'location_country',
  'location_state',
  'location_zip',
  'money',
  'name',
  'name_given',
  'name_family',
  'name_medical_professional',
  'numerical_pii',
  'occupation',
  'ordinal',
  'origin',
  'passport_number',
  'password',
  'percent',
  'phone_number',
  'physical_attribute',
  'ssn',
  'time',
  'url',
  'username',
  'vehicle_id',
])
export type RedactOption = z.infer<typeof RedactOptionSchema>

export type RedactOptionItem = {
  value: RedactOption
  label: string
  description: string
}

export const RedactOptions: RedactOptionItem[] = [
  {
    value: 'account_number',
    label: 'Account Number',
    description: 'Customer account or membership identification number',
  },
  {
    value: 'age',
    label: 'Age',
    description: "Numbers associated with an individual's age",
  },
  {
    value: 'bank_account',
    label: 'Bank Account',
    description: 'Bank account numbers and international equivalents, such as IBAN',
  },
  {
    value: 'cardinal',
    label: 'Cardinal Number',
    description: 'Numerical expressions in cardinal form',
  },
  {
    value: 'credit_card',
    label: 'Credit Card',
    description: 'Credit card numbers',
  },
  {
    value: 'credit_card_expiration',
    label: 'Credit Card Expiration',
    description: 'Expiration date of a credit card',
  },
  {
    value: 'cvv',
    label: 'CVV',
    description: '3- or 4-digit card verification codes and equivalents',
  },
  {
    value: 'date',
    label: 'Date',
    description: 'Specific calendar dates, which can include days of the week, dates, months, or years',
  },
  {
    value: 'date_interval',
    label: 'Date Interval',
    description: 'Broader time periods, including date ranges, months, seasons, years, and decades',
  },
  {
    value: 'dob',
    label: 'Date of Birth',
    description: 'Dates of birth',
  },
  {
    value: 'driver_license',
    label: 'Driver License',
    description: "Driver's permit numbers",
  },
  {
    value: 'email_address',
    label: 'Email Address',
    description: 'Email addresses',
  },
  {
    value: 'event',
    label: 'Event',
    description: 'Names of events or holidays',
  },
  {
    value: 'filename',
    label: 'Filename',
    description: 'Names of computer files, including the extension or filepath',
  },
  {
    value: 'gender_sexuality',
    label: 'Gender & Sexuality',
    description: 'Terms indicating gender identity or sexual orientation, including slang terms',
  },
  {
    value: 'healthcare_number',
    label: 'Healthcare Number',
    description: 'Healthcare numbers and health plan beneficiary numbers',
  },
  {
    value: 'ip_address',
    label: 'IP Address',
    description: 'Internet IP address, including IPv4 and IPv6 formats',
  },
  {
    value: 'location_address',
    label: 'Location Address',
    description: 'Full or partial physical mailing addresses',
  },
  {
    value: 'location_city',
    label: 'Location City',
    description: 'Municipality names, including villages, towns, and cities',
  },
  {
    value: 'location_coordinate',
    label: 'Location Coordinate',
    description: 'Geographic positions referred to using latitude, longitude, and/or elevation coordinates',
  },
  {
    value: 'location_country',
    label: 'Location Country',
    description: 'Country names',
  },
  {
    value: 'location_state',
    label: 'Location State',
    description: 'State, province, territory, or prefecture names',
  },
  {
    value: 'location_zip',
    label: 'Location Zip Code',
    description: 'Zip codes (including Zip+4), postcodes, or postal codes',
  },
  {
    value: 'money',
    label: 'Money',
    description: 'Names and/or amounts of currency',
  },
  {
    value: 'name',
    label: 'Name',
    description: "Names of individuals, not including personal titles such as 'Mrs.' or 'Mr.'",
  },
  {
    value: 'name_given',
    label: 'Given Name',
    description:
      'Names given to an individual, usually at birth; often first / middle names in Western cultures and middle / last names in Eastern cultures',
  },
  {
    value: 'name_family',
    label: 'Family Name',
    description:
      "Names indicating a person's family or community; often a last name in Western cultures and first name in Eastern cultures",
  },
  {
    value: 'name_medical_professional',
    label: 'Medical Professional Name',
    description: 'Names including the title of a medical professional, such a “Doctor”',
  },
  {
    value: 'numerical_pii',
    label: 'Numerical PII',
    description: "Numerical PII that doesn't fall under other categories",
  },
  {
    value: 'occupation',
    label: 'Occupation',
    description: 'Job titles or professions',
  },
  {
    value: 'ordinal',
    label: 'Ordinal Number',
    description: 'Numerical expressions in ordinal form',
  },
  {
    value: 'origin',
    label: 'Origin',
    description: 'Terms indicating nationality, ethnicity, or provenance',
  },
  {
    value: 'passport_number',
    label: 'Passport Number',
    description: 'Passport numbers, issued by any country',
  },
  {
    value: 'password',
    label: 'Password',
    description: 'Account passwords, PINs, access keys, or verification answers',
  },
  {
    value: 'percent',
    label: 'Percent',
    description: 'Numerical expressions as percentages',
  },
  {
    value: 'phone_number',
    label: 'Phone Number',
    description: 'Telephone or fax numbers',
  },
  {
    value: 'physical_attribute',
    label: 'Physical Attribute',
    description: 'Distinctive bodily attributes, including terms indicating race',
  },
  {
    value: 'ssn',
    label: 'SSN',
    description: 'Social Security Numbers or international equivalent government identification numbers',
  },
  {
    value: 'time',
    label: 'Time',
    description: 'Expressions indicating clock times',
  },
  {
    value: 'url',
    label: 'URL',
    description: 'Internet addresses',
  },
  {
    value: 'username',
    label: 'Username',
    description: 'Usernames, login names, or handles',
  },
  {
    value: 'vehicle_id',
    label: 'Vehicle ID',
    description: 'Vehicle identification numbers (VINs), vehicle serial numbers, and license plate numbers',
  },
]
